/**
 * Imports
 */
import * as React from 'react';

import { styled } from 'theme';
import { Button, Input, Select } from 'components';
import { Col, Form, message, Row } from 'antd';
// @ts-ignore
import ReactCountryFlag from 'react-country-flag';
import { createMerchant } from 'apis';
import { IBAN_REGEX } from './iban-examples';
/**
 * Styling
 */
const Root = styled.div`
  width: 100%;
  max-width: 480px;
`;
const StyledSelect = styled(Select)`
  && {
    min-width: 100%;
    .ant-select-selector .ant-select-selection-search-input {
      height: 42px;
    }
    .ant-select-arrow svg {
      fill: ${({ theme }) => theme.colors.Text.UI};
    }
  }
`;
const StyledOption = styled(Select.Option)``;

/**
 * Types
 */
export interface CreateMerchantFormProps {}
export type MerchantFormRow = {
  merchantStoreName: string;
  merchantName: string;
  registrationNumber: string;
  descriptor: string;
  mcc: string;
  // currency: string;
  addressLine1: string;
  city: string;
  zip: string;
  country: string;
  timezone: string;
  name: string;
  email: string;
  phone: string;
  iban: string;
  swift: string;
  averageTransactionAmount: string;
  monthlyTurnover: string;
  businessActivityDescription: string;
  clearhausMerchantId: string;
  password: string;
};
export type MerchantFaroeIslandFormData = {
  merchantStoreName: string;
  merchantName: string;
  registrationNumber: string;
  descriptor: string;
  currency: string;
  address: {
    addressLine1: string;
    city: string;
    zip: string;
    country: string;
    timezone: string;
  };
  contact: {
    name: string;
    email: string;
    phone: string;
  };
  bank: {
    iban: string;
  };
  businessActivityDescription: string;
  password: string;
  averageTransactionAmount: number;
  monthlyTurnover: number;
  website: string;
};
export type MerchantGreenlandFormData = {
  merchantStoreName: string;
  merchantName: string;
  registrationNumber: string;
  descriptor: string;
  currency: string;
  address: {
    addressLine1: string;
    city: string;
    zip: string;
    country: string;
    timezone: string;
  };
  contact: {
    name: string;
    email: string;
    phone: string;
  };
  bank: {
    iban: string;
  };
  businessActivityDescription: string;
  password: string;
  averageTransactionAmount: number;
  monthlyTurnover: number;
  website: string;
};

export type MerchantFormData = {
  merchantStoreName: string;
  merchantName: string;
  registrationNumber: string;
  descriptor: string;
  mcc: string;
  currency: string;
  address: {
    addressLine1: string;
    city: string;
    zip: string;
    country: string;
    timezone: string;
  };
  contact: {
    name: string;
    email: string;
    phone: string;
  };
  bank: {
    iban: string;
    bic: string;
  };
  clearhausMerchantId: string;
  businessActivityDescription: string;
  password: string;
  averageTransactionAmount: number;
  monthlyTurnover: number;
  commissionProfileCode: string;
  merchantContract: string;
  website: string;
};

export const MERCHANT_FORM_FIELDS: {
  [key in keyof MerchantFormRow]: keyof MerchantFormRow;
} = {
  merchantStoreName: 'merchantStoreName',
  merchantName: 'merchantName',
  registrationNumber: 'registrationNumber',
  descriptor: 'descriptor',
  mcc: 'mcc',
  addressLine1: 'addressLine1',
  city: 'city',
  zip: 'zip',
  country: 'country',
  timezone: 'timezone',
  name: 'name',
  email: 'email',
  phone: 'phone',
  iban: 'iban',
  swift: 'swift',
  averageTransactionAmount: 'averageTransactionAmount',
  monthlyTurnover: 'monthlyTurnover',
  businessActivityDescription: 'businessActivityDescription',
  clearhausMerchantId: 'clearhausMerchantId',
  password: 'password',
};
const COUNTRY_CODES: { [key in string]: { name: string } } = {
  CY: {
    name: 'Cyprus',
  },
  GR: {
    name: 'Greece',
  },
};
const TIMEZONE_MAP: { [key in string]: string } = {
  CY: 'Asia/Nicosia',
  GR: 'Europe/Athens',
};
/**
 * Example data
 */
//  {
//   "merchantStoreName": "T. & A. Discount Market Ltd",
//   "merchantName": "T. & A. Discount Market Ltd",
//   "registrationNumber": "HE169925",
//   "descriptor": "T. & A. Discount",
//   "mcc": "5331",
//   "currency": "EUR",
//   "address": {
//       "addressLine1": "Arsinois, 6-10",
//       "city": "Nicosia",
//       "zip": "1010",
//       "country": "CY",
//       "timezone": "Asia/Nicosia"
//   },
//   "contact": {
//       "name": "Agnieszka Izabella Diakopoulos",
//       "email": "agnieszkadiakopoulos@icloud.com",
//       "phone": "NA"
//   },
//   "bank": {
//       "iban": "CY08002001500000001100467600",
//       "bic": "BCYPCY2N"
//   },
//   "merchantContract": "l4p_softpos_intro_cy",
//   "clearhausMerchantId": "2029102",
//   "commissionProfileCode": "softposL4PIntroCY",
//   "averageTransactionAmount": 100,
//   "businessActivityDescription": "The merchant has a kiosk, convenience store.",
//   "monthlyTurnover": 500000,
//   "website": "www.link4pay.com",
//   "password": "AgNies2021"
// }
const CreateMerchantForm: React.FC<CreateMerchantFormProps> = (_props) => {
  const [isValid, setIsValid] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [form] = Form.useForm();

  const _reset = () => {
    form.resetFields();
  };

  const _isValid = (allValues: MerchantFormRow) => {
    if (
      allValues.merchantStoreName &&
      allValues.merchantName &&
      allValues.registrationNumber &&
      allValues.descriptor &&
      allValues.mcc &&
      allValues.addressLine1 &&
      allValues.city &&
      allValues.zip &&
      allValues.country &&
      allValues.name &&
      allValues.email &&
      allValues.phone &&
      allValues.iban &&
      allValues.swift
    ) {
      setIsValid(true);
    } else {
      setIsValid(false);
    }
  };

  const _onChange = (_values: MerchantFormRow, allValues: MerchantFormRow) => {
    _isValid(allValues);
  };

  const _onFinish = async (values: MerchantFormRow) => {
    setIsLoading(true);
    try {
      const data: MerchantFormData = {
        merchantStoreName: values.merchantStoreName,
        merchantName: values.merchantName,
        registrationNumber: values.registrationNumber,
        descriptor: values.descriptor,
        mcc: values.mcc,
        address: {
          addressLine1: values.addressLine1,
          city: values.city,
          zip: values.zip,
          country: values.country,
          timezone: TIMEZONE_MAP[values.country],
        },
        contact: {
          name: values.name,
          email: values.email,
          phone: values.phone,
        },
        bank: {
          iban: values.iban,
          bic: values.swift,
        },
        averageTransactionAmount: parseInt(values.averageTransactionAmount),
        businessActivityDescription: values.businessActivityDescription,
        clearhausMerchantId: values.clearhausMerchantId,
        monthlyTurnover: parseInt(values.monthlyTurnover),
        password: values.password,
        // Hardcoded values
        currency: 'EUR',
        merchantContract: 'l4p_softpos_intro_cy',
        commissionProfileCode: 'softposL4PIntroCY',
        website: 'www.link4pay.com',
      };
      const resp = await createMerchant(data);
      if (resp.success) {
        message.success('Merchant succesfully created');
        _reset();
      }
    } catch (error) {
      message.error(error);
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <Root>
      <Form
        layout="vertical"
        onFinish={_onFinish}
        onValuesChange={_onChange}
        form={form}
      >
        {/* merchantStoreName */}
        <Form.Item
          name={MERCHANT_FORM_FIELDS.merchantStoreName}
          label="Merchant Store Name"
        >
          <Input placeholder="Fernworth Consulting Ltd" />
        </Form.Item>
        {/* merchantName */}
        <Form.Item
          name={MERCHANT_FORM_FIELDS.merchantName}
          label="Merchant Name"
        >
          <Input placeholder="Fernworth Consulting" />
        </Form.Item>
        {/* registrationNumber */}
        <Form.Item
          name={MERCHANT_FORM_FIELDS.registrationNumber}
          label="Registration Number"
        >
          <Input placeholder="02193074" />
        </Form.Item>
        {/* descriptor */}
        <Form.Item name={MERCHANT_FORM_FIELDS.descriptor} label="Descriptor">
          <Input placeholder="Fernworth - SoftPOS" />
        </Form.Item>
        {/* mcc */}
        <Form.Item name={MERCHANT_FORM_FIELDS.mcc} label="Mcc">
          <Input placeholder="5499" />
        </Form.Item>
        {/* currency */}
        {/* <Form.Item name={MERCHANT_FORM_FIELDS.currency} label="currency">
          <Input placeholder="EUR" defaultValue="EUR" />
        </Form.Item> */}
        {/* addressLine1 */}
        <Form.Item
          name={MERCHANT_FORM_FIELDS.addressLine1}
          label="Address Line"
        >
          <Input placeholder="60 Limassol Avenue" />
        </Form.Item>
        <Row gutter={8}>
          {/* city */}
          <Col span={12}>
            <Form.Item name={MERCHANT_FORM_FIELDS.city} label="City">
              <Input placeholder="Nicosia" />
            </Form.Item>
          </Col>
          {/* zip */}
          <Col span={12}>
            <Form.Item name={MERCHANT_FORM_FIELDS.zip} label="Zip">
              <Input placeholder="2014" />
            </Form.Item>
          </Col>
        </Row>
        {/* country */}
        <Form.Item name={MERCHANT_FORM_FIELDS.country} label="Country">
          <StyledSelect placeholder="Select country">
            {Object.entries(COUNTRY_CODES).map((c) => {
              return (
                <StyledOption key={c[0]} value={c[0]}>
                  <ReactCountryFlag
                    style={{
                      fontSize: '18px',
                      lineHeight: '18px',
                      marginRight: '8px',
                      marginTop: '-4px',
                    }}
                    countryCode={c[0]}
                    svg
                  />
                  {c[1].name}
                </StyledOption>
              );
            })}
          </StyledSelect>
        </Form.Item>
        {/* name */}
        <Form.Item name={MERCHANT_FORM_FIELDS.name} label="Name">
          <Input placeholder="Theodoros Savvides" />
        </Form.Item>
        {/* email */}
        <Form.Item name={MERCHANT_FORM_FIELDS.email} label="E-mail">
          <Input placeholder="theodoros.savvides@link4pay.com" />
        </Form.Item>
        {/* phone */}
        <Form.Item name={MERCHANT_FORM_FIELDS.phone} label="Phone">
          <Input placeholder="+357 91 234567" />
        </Form.Item>
        {/* iban */}
        <Form.Item
          name={MERCHANT_FORM_FIELDS.iban}
          label="IBAN"
          rules={[
            {
              pattern: IBAN_REGEX,
              required: true,
              message: 'Please add a valid IBAN',
            },
          ]}
        >
          <Input placeholder="LT383250039345589756" />
        </Form.Item>
        {/* swift */}
        <Form.Item name={MERCHANT_FORM_FIELDS.swift} label="SWIFT/BIC">
          <Input placeholder="ABKLCY2N" />
        </Form.Item>

        {/* averageTransactionAmount */}
        <Form.Item
          name={MERCHANT_FORM_FIELDS.averageTransactionAmount}
          label="Average Transaction Amount"
        >
          <Input placeholder="50" type="number" />
        </Form.Item>
        {/* monthlyTurnover */}
        <Form.Item
          name={MERCHANT_FORM_FIELDS.monthlyTurnover}
          label="Monthly Turnover"
        >
          <Input placeholder="500000" type="number" />
        </Form.Item>
        {/* businessActivityDescription */}
        <Form.Item
          name={MERCHANT_FORM_FIELDS.businessActivityDescription}
          label="Business Activity Description"
        >
          <Input placeholder="Description" />
        </Form.Item>
        {/* clearhausMerchantId */}
        <Form.Item
          name={MERCHANT_FORM_FIELDS.clearhausMerchantId}
          label="Clearhaus Merchant Id"
        >
          <Input placeholder="2029102" />
        </Form.Item>
        {/* password */}
        <Form.Item name={MERCHANT_FORM_FIELDS.password} label="Password">
          <Input placeholder="AgNies2021" />
        </Form.Item>
        {/* submit */}
        <Form.Item>
          <Button
            loading={isLoading}
            type="primary"
            disabled={!isValid}
            htmlType="submit"
          >
            Add Merchant
          </Button>
        </Form.Item>
      </Form>
    </Root>
  );
};

/**
 * Exports
 */
export { CreateMerchantForm };
